import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { IafPassSvc, IafItemSvc, IafFetch } from '../../services/platformApi';
import {
  SUGGESTED_INVITE_TEMPLATE,
  SUGGESTED_REMINDER_TEMPLATE,
  type NotificationTemplate,
} from '../InviteTemplateManagement/templateConstants';

// Sample values used only for template preview rendering
const PREVIEW_PARAMS: Record<string, string> = {
  base_url: 'https://sandbox-api.invicara.com',
  subject: "You've been invited to Twinit Academy Training Application",
  body_header: 'Twinit Academy Training Application',
  name: 'Acme Corp Developer Training',
  body_content:
    "<p>In the next few minutes you will be receiving a few invitations to various Twinit resources including Twinit Academy.</p>",
  inviter_name: 'Invicara Developer Team',
  invite_link: 'https://sandbox.invicara.com/console#preview-placeholder',
  host: 'sandbox.invicara.com',
};
import './OnboardingSettingsPage.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ONBOARDING_SETTINGS_USER_TYPE = 'onboarding_settings';

interface OnboardingSetting {
  _id: string;
  _appId: string;
  _appName: string;
  hasInviteTemplate: boolean;
  templateContent: string | null;
  reminderTemplateContent: string | null;
  templateTitle?: string;
  reminderTemplateTitle?: string;
  createdAt: string;
  updatedAt: string;
}

interface Organization {
  _id: string;
  _name: string;
  _description?: string;
}

interface PassportApp {
  _id: string;
  _name: string;
  _shortName?: string;
  _description?: string;
  _orgId?: string;
  _namespaces?: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardingSettingsPage() {
  const { accessToken, createCtx } = useAuth();

  // Org / app hierarchy
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [appsByOrg, setAppsByOrg] = useState<Record<string, PassportApp[]>>({});
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());

  // Settings
  const [settings, setSettings] = useState<OnboardingSetting[]>([]);
  const [selectedApp, setSelectedApp] = useState<PassportApp | null>(null);
  const [selectedSetting, setSelectedSetting] = useState<OnboardingSetting | null>(null);

  // Loading
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Template editor
  const [isEditing, setIsEditing] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editorReminderContent, setEditorReminderContent] = useState('');

  // Preview — computed via srcDoc, no ref needed

  // Live template from Passport
  const [liveTemplates, setLiveTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Collection reference
  const [settingsCollectionId, setSettingsCollectionId] = useState<string | null>(null);

  // ---- Pagination helper for Passport endpoints -------------------------

  const fetchAllPages = useCallback(async <T,>(
    url: string,
    pageSize = 100,
  ): Promise<T[]> => {
    const results: T[] = [];
    let offset = 0;
    let total = Infinity;

    while (results.length < total) {
      const sep = url.includes('?') ? '&' : '?';
      const pagedUrl = `${url}${sep}_pageSize=${pageSize}&_offset=${offset}`;

      const resp = await fetch(pagedUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      if (data._list) {
        results.push(...data._list);
        total = data._total ?? data._list.length;
        offset += pageSize;
        if (data._list.length === 0) break;
      } else {
        break;
      }
    }
    return results;
  }, [accessToken]);

  // ---- Passport notification template helpers (via SDK) ------------------
  // Query by _appId (works), then filter by _name client-side.
  // Querying by _name alone returns empty in this app context.

  const getTemplates = useCallback(async (
    name: string, appId: string,
  ): Promise<NotificationTemplate[]> => {
    try {
      const result = await IafPassSvc.getNotificationTemplates(
        { _appId: appId }, createCtx(),
      );
      const all = result?._list || [];
      return all.filter(t => t._name === name);
    } catch (err) {
      console.warn('getTemplates failed for', name, appId, err);
      return [];
    }
  }, [createCtx]);

  // ---- Load orgs, apps, and settings ------------------------------------

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = IafFetch.CONFIG.passportServiceOrigin;

      // Fetch all organizations (paginated)
      const allOrgs = await fetchAllPages<Organization>(
        `${baseUrl}/passportsvc/api/v1/organizations`,
      );
      setOrgs(allOrgs);

      // Fetch apps per organization (paginated)
      const orgApps: Record<string, PassportApp[]> = {};
      await Promise.all(
        allOrgs.map(async (org) => {
          const apps = await fetchAllPages<PassportApp>(
            `${baseUrl}/passportsvc/api/v1/apps?_orgId=${org._id}`,
          );
          orgApps[org._id] = apps;
        }),
      );
      setAppsByOrg(orgApps);

      // Auto-expand all orgs if there are 5 or fewer
      if (allOrgs.length <= 5) {
        setExpandedOrgs(new Set(allOrgs.map(o => o._id)));
      }

      // Load onboarding settings
      const baseCtxForSettings = createCtx();
      const settingsCollectionResp = await IafItemSvc.getNamedUserItems({
        query: { _userType: ONBOARDING_SETTINGS_USER_TYPE },
      }, baseCtxForSettings);

      const settingsResults: OnboardingSetting[] = [];

      if (settingsCollectionResp?._list?.length) {
        const settingsCollection = settingsCollectionResp._list[0];
        setSettingsCollectionId(settingsCollection._userItemId as string);

        let sOffset = 0;
        const pageSize = 100;
        let sTotal = 0;
        let first = true;

        while (first || settingsResults.length < sTotal) {
          first = false;
          const items = await IafItemSvc.getRelatedItems(
            settingsCollection._userItemId as string,
            {},
            baseCtxForSettings,
            { page: { _offset: sOffset, _pageSize: pageSize } },
          );

          if (items._list) {
            sTotal = items._total;
            sOffset += pageSize;
            settingsResults.push(...(items._list as unknown as OnboardingSetting[]));
          } else {
            break;
          }
        }
      } else {
        setSettingsCollectionId(null);
      }

      setSettings(settingsResults);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load data: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, fetchAllPages, createCtx]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- Settings collection helper ----------------------------------------

  const ensureSettingsCollection = useCallback(async (): Promise<string | null> => {
    if (settingsCollectionId) return settingsCollectionId;

    const ctx = createCtx();
    try {
      const resp = await IafItemSvc.getNamedUserItems({
        query: { _userType: ONBOARDING_SETTINGS_USER_TYPE },
      }, ctx);

      if (resp?._list?.length) {
        const id = resp._list[0]._userItemId as string;
        setSettingsCollectionId(id);
        return id;
      }

      const createResp = await IafItemSvc.createNamedUserItems([{
        _name: 'Onboarding Settings',
        _shortName: 'onboarding_settings',
        _description: 'Tracks invite template assignments per training application',
        _userType: ONBOARDING_SETTINGS_USER_TYPE,
        _namespaces: ctx._namespaces || [],
      }], 'NamedUserCollection', ctx);

      const id = (createResp?._list?.[0]?._userItemId as string) || null;
      setSettingsCollectionId(id);
      return id;
    } catch {
      return null;
    }
  }, [settingsCollectionId, createCtx]);

  // ---- Resolve Velocity variables for preview ---------------------------

  const resolvePreview = useCallback((html: string): string => {
    const params: Record<string, string> = { ...PREVIEW_PARAMS };

    let resolved = html;
    for (const [key, value] of Object.entries(params)) {
      resolved = resolved.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value || '');
      resolved = resolved.replace(new RegExp(`\\$!\\{${key}\\}`, 'g'), value || '');
    }
    // Velocity #if: render block when var is truthy
    resolved = resolved.replace(
      /#if\s*\([^)]*\$(\w+)[^)]*\)([\s\S]*?)#end/g,
      (_match, varName, block) => {
        const val = params[varName];
        return val && val.trim() ? block : '';
      },
    );
    for (const [key, value] of Object.entries(params)) {
      resolved = resolved.replace(new RegExp(`\\$${key}(?![\\w])`, 'g'), value || '');
    }
    resolved = resolved.replace(/\$\{[^}]+\}/g, '');
    resolved = resolved.replace(/\$!\{[^}]+\}/g, '');
    return resolved;
  }, []);

  // ---- Select app --------------------------------------------------------

  const selectApp = useCallback(async (app: PassportApp) => {
    setSelectedApp(app);
    setIsEditing(false);
    setSuccessMessage(null);
    setError(null);
    setLiveTemplates([]);

    const appId = app._id;
    if (!appId) return;

    const setting = settings.find(s => s._appId === appId) || null;
    setSelectedSetting(setting);

    setEditorContent(setting?.templateContent || SUGGESTED_INVITE_TEMPLATE);
    setEditorReminderContent(setting?.reminderTemplateContent || SUGGESTED_REMINDER_TEMPLATE);

    setIsLoadingLive(true);
    try {
      const [inviteList, reminderList] = await Promise.all([
        getTemplates('USER_GROUP_INVITE', appId),
        getTemplates('USER_GROUP_INVITE_REMINDER', appId),
      ]);
      const live = [...inviteList, ...reminderList];
      setLiveTemplates(live);

      const liveInvite = live.find(t => t._name === 'USER_GROUP_INVITE');
      const liveReminder = live.find(t => t._name === 'USER_GROUP_INVITE_REMINDER');
      if (liveInvite) setEditorContent(liveInvite._message);
      if (liveReminder) setEditorReminderContent(liveReminder._message);
    } catch {
      setLiveTemplates([]);
    } finally {
      setIsLoadingLive(false);
    }
  }, [settings, getTemplates]);

  // ---- Deploy template ---------------------------------------------------

  const deployTemplate = async () => {
    if (!selectedApp) return;
    const appId = selectedApp._id;
    const appName = selectedApp._name || '';
    if (!appId) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const baseCtx = createCtx();
      const collectionId = await ensureSettingsCollection();
      if (!collectionId) throw new Error('Could not access onboarding settings collection');

      // Deploy invite template
      const ctx = createCtx();
      const passportBase = IafFetch.CONFIG.passportServiceOrigin + '/passportsvc/api/v1';
      const existingInvite = await getTemplates('USER_GROUP_INVITE', appId);
      console.log('[deploy] USER_GROUP_INVITE for app', appId, '- found:', existingInvite.length, existingInvite.map(t => t._id));

      if (existingInvite.length > 0) {
        console.log('[deploy] PUT to', existingInvite[0]._id);
        await IafPassSvc.updateNotificationTemplate(existingInvite[0]._id, {
          _name: 'USER_GROUP_INVITE',
          _title: 'Welcome to ${name}',
          _message: editorContent,
          _format: 'text/html',
          _appId: appId,
        }, ctx);
      } else {
        // POST with _appId as query param (undocumented) to create in target app
        console.log('[deploy] POST new template for app', appId);
        const createResp = await fetch(
          `${passportBase}/notification_templates?_appId=${encodeURIComponent(appId)}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify([{
              _name: 'USER_GROUP_INVITE',
              _title: 'Welcome to ${name}',
              _message: editorContent,
              _format: 'text/html',
            }]),
          },
        );
        if (!createResp.ok) {
          const err = await createResp.json().catch(() => ({}));
          throw new Error(err.message || `Create failed: HTTP ${createResp.status}`);
        }
      }

      // Deploy reminder template
      if (editorReminderContent.trim()) {
        const existingReminder = await getTemplates('USER_GROUP_INVITE_REMINDER', appId);
        console.log('[deploy] USER_GROUP_INVITE_REMINDER for app', appId, '- found:', existingReminder.length);

        if (existingReminder.length > 0) {
          await IafPassSvc.updateNotificationTemplate(existingReminder[0]._id, {
            _name: 'USER_GROUP_INVITE_REMINDER',
            _title: 'Reminder: Invitation to ${name}',
            _message: editorReminderContent,
            _format: 'text/html',
            _appId: appId,
          }, ctx);
        } else {
          const createResp = await fetch(
            `${passportBase}/notification_templates?_appId=${encodeURIComponent(appId)}`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify([{
                _name: 'USER_GROUP_INVITE_REMINDER',
                _title: 'Reminder: Invitation to ${name}',
                _message: editorReminderContent,
                _format: 'text/html',
              }]),
            },
          );
          if (!createResp.ok) {
            const err = await createResp.json().catch(() => ({}));
            throw new Error(err.message || `Create reminder failed: HTTP ${createResp.status}`);
          }
        }
      }

      // Update onboarding settings collection
      const existingSetting = settings.find(s => s._appId === appId);
      if (existingSetting) {
        await IafItemSvc.updateRelatedItem(
          collectionId, existingSetting._id,
          {
            hasInviteTemplate: true,
            templateContent: editorContent,
            reminderTemplateContent: editorReminderContent || null,
            templateTitle: 'Welcome to ${name}',
            reminderTemplateTitle: 'Reminder: Invitation to ${name}',
            updatedAt: new Date().toISOString(),
          },
          baseCtx,
        );
      } else {
        await IafItemSvc.createRelatedItems(
          collectionId,
          [{
            _appId: appId,
            _appName: appName,
            hasInviteTemplate: true,
            templateContent: editorContent,
            reminderTemplateContent: editorReminderContent || null,
            templateTitle: 'Welcome to ${name}',
            reminderTemplateTitle: 'Reminder: Invitation to ${name}',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }],
          baseCtx,
        );
      }

      setSuccessMessage('Template deployed to Passport!');
      setIsEditing(false);
      await loadData();
      await selectApp(selectedApp);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to deploy template: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Reset to training template ----------------------------------------

  const resetToTrainingTemplate = () => {
    setEditorContent(SUGGESTED_INVITE_TEMPLATE);
    setEditorReminderContent(SUGGESTED_REMINDER_TEMPLATE);
  };

  // ---- Remove template ---------------------------------------------------

  const removeTemplate = async () => {
    if (!selectedApp) return;
    const appId = selectedApp._id;
    if (!appId) return;

    if (!window.confirm('Remove the invite template from this app? The Passport templates will be deleted.')) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const baseCtx = createCtx();

      const ctx = createCtx();
      const inviteList = await getTemplates('USER_GROUP_INVITE', appId);
      for (const t of inviteList) {
        await IafPassSvc.deleteNotificationTemplate(t._id, ctx);
      }

      const reminderList = await getTemplates('USER_GROUP_INVITE_REMINDER', appId);
      for (const t of reminderList) {
        await IafPassSvc.deleteNotificationTemplate(t._id, ctx);
      }

      const existingSetting = settings.find(s => s._appId === appId);
      if (existingSetting && settingsCollectionId) {
        await IafItemSvc.updateRelatedItem(
          settingsCollectionId, existingSetting._id,
          {
            hasInviteTemplate: false,
            templateContent: null,
            reminderTemplateContent: null,
            updatedAt: new Date().toISOString(),
          },
          baseCtx,
        );
      }

      setSuccessMessage('Templates removed from Passport.');
      await loadData();
      await selectApp(selectedApp);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to remove template: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Preview HTML — resolved on every render so srcDoc always has fresh content
  const previewHtml = editorContent ? resolvePreview(editorContent) : '';

  // ---- Org expand toggle -------------------------------------------------

  const toggleOrg = (orgId: string) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  // ---- Helpers -----------------------------------------------------------

  const getSettingForApp = (appId: string) => settings.find(s => s._appId === appId);

  const totalApps = Object.values(appsByOrg).reduce((sum, list) => sum + list.length, 0);

  const templateHasTrainingSections = editorContent.includes('#if($resources_section')
    || editorContent.includes('#if($support_section')
    || editorContent.includes('#if($summary_section');

  // ---- Render ------------------------------------------------------------

  return (
    <div className="onboarding-settings">
      <div className="obs-header">
        <h2>App Invite Templates</h2>
        <p className="obs-subtitle">
          Each training application needs an invite email template deployed to Passport
          so that users receive a welcome email when they are added to the app.
          Select an app below to deploy or edit its invite template.
        </p>
      </div>

      {error && <div className="obs-error">{error}</div>}
      {successMessage && <div className="obs-success">{successMessage}</div>}

      <div className="obs-layout">
        {/* Left panel — org / app tree */}
        <div className="obs-app-list">
          <h3>Organizations &amp; Applications</h3>

          {isLoading ? (
            <div className="obs-loading">Loading organizations and applications...</div>
          ) : orgs.length === 0 ? (
            <div className="obs-empty">
              No organizations found. Make sure you have permission to list organizations.
            </div>
          ) : (
            <div className="obs-tree">
              <div className="obs-tree-summary">
                {orgs.length} org{orgs.length !== 1 ? 's' : ''}, {totalApps} app{totalApps !== 1 ? 's' : ''}
              </div>

              {orgs.map(org => {
                const orgApps = appsByOrg[org._id] || [];
                const isExpanded = expandedOrgs.has(org._id);

                return (
                  <div key={org._id} className="obs-org-group">
                    <button
                      className="obs-org-toggle"
                      onClick={() => toggleOrg(org._id)}
                    >
                      <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                      <span className="obs-org-name">{org._name}</span>
                      <span className="obs-org-count">{orgApps.length}</span>
                    </button>

                    {isExpanded && (
                      <div className="obs-org-apps">
                        {orgApps.length === 0 ? (
                          <div className="obs-org-empty">No applications</div>
                        ) : (
                          orgApps.map(app => {
                            const setting = getSettingForApp(app._id);
                            const isSelected = selectedApp?._id === app._id;

                            return (
                              <div
                                key={app._id}
                                className={`obs-card ${isSelected ? 'obs-card--selected' : ''}`}
                                onClick={() => selectApp(app)}
                              >
                                <div className="obs-card-name">{app._name}</div>
                                <div className="obs-card-status">
                                  {setting?.hasInviteTemplate ? (
                                    <span className="obs-badge obs-badge--assigned">Template Assigned</span>
                                  ) : (
                                    <span className="obs-badge obs-badge--none">No Template</span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right panel — details */}
        <div className="obs-detail">
          {!selectedApp ? (
            <div className="obs-detail-empty">
              Select an application from the list to view and deploy its invite template.
            </div>
          ) : (
            <>
              <h3>{selectedApp._name}</h3>
              <div className="obs-detail-meta">
                <span className="obs-meta-label">App ID:</span>
                <code>{selectedApp._id}</code>
                {liveTemplates.length > 0 ? (
                  <span className="obs-badge obs-badge--assigned">Deployed</span>
                ) : (
                  <span className="obs-badge obs-badge--none">Not Deployed</span>
                )}
                {selectedSetting?.updatedAt && (
                  <span className="obs-meta-date">
                    Last deployed: {new Date(selectedSetting.updatedAt).toLocaleString()}
                  </span>
                )}
              </div>

              {isLoadingLive ? (
                <div className="obs-loading">Loading template...</div>
              ) : (
                <div className="obs-template-view">
                  {/* Action bar */}
                  <div className="obs-actions">
                    {!isEditing ? (
                      <>
                        <button
                          className="obs-btn obs-btn--secondary"
                          onClick={() => setIsEditing(true)}
                        >
                          Edit Before Deploying
                        </button>
                        <button
                          className="obs-btn obs-btn--primary"
                          onClick={deployTemplate}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Deploying...' : 'Deploy to App'}
                        </button>
                        {liveTemplates.length > 0 && (
                          <button
                            className="obs-btn obs-btn--danger"
                            onClick={removeTemplate}
                            disabled={isSaving}
                          >
                            Remove
                          </button>
                        )}
                        <button
                          className="obs-btn obs-btn--outline"
                          onClick={resetToTrainingTemplate}
                          title="Reset the template content back to the pre-built training template"
                        >
                          Reset to Training Template
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="obs-btn obs-btn--primary"
                          onClick={deployTemplate}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Deploying...' : 'Deploy to App'}
                        </button>
                        <button
                          className="obs-btn obs-btn--outline"
                          onClick={() => setIsEditing(false)}
                        >
                          Done Editing
                        </button>
                        <button
                          className="obs-btn obs-btn--outline"
                          onClick={resetToTrainingTemplate}
                          title="Reset the template content back to the pre-built training template"
                        >
                          Reset to Training Template
                        </button>
                        {liveTemplates.length > 0 && (
                          <button
                            className="obs-btn obs-btn--danger"
                            onClick={removeTemplate}
                            disabled={isSaving}
                          >
                            Remove
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Warning: live template missing training sections */}
                  {liveTemplates.length > 0 && !templateHasTrainingSections && (
                    <div className="obs-template-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <div>
                        <strong>Outdated template</strong> — the deployed template is missing training-specific
                        sections (resources, support, summary). Click <em>Reset to Training Template</em> above
                        then <em>Deploy to App</em> to upgrade it.
                      </div>
                    </div>
                  )}

                  {/* Editable textareas (only shown in edit mode) */}
                  {isEditing && (
                    <div className="obs-editor">
                      <h4>Invite Template (USER_GROUP_INVITE)</h4>
                      <textarea
                        className="obs-editor-textarea"
                        value={editorContent}
                        onChange={e => setEditorContent(e.target.value)}
                        rows={20}
                      />

                      <h4>Reminder Template (USER_GROUP_INVITE_REMINDER)</h4>
                      <textarea
                        className="obs-editor-textarea"
                        value={editorReminderContent}
                        onChange={e => setEditorReminderContent(e.target.value)}
                        rows={12}
                      />
                    </div>
                  )}

                  {/* Always-visible preview */}
                  <div className="obs-preview">
                    <h4>Preview</h4>
                    <iframe
                      srcDoc={previewHtml}
                      title="Template Preview"
                      sandbox="allow-same-origin"
                      className="obs-preview-iframe"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
